/**
 * File: useKrokiRenderer.ts
 * Description: Hook for rendering Structurizr diagrams via Kroki service
 * Author: System
 * Created: 2025-01-14
 * Last Modified: 2025-01-14
 */

import { useCallback, useState } from 'react';

import { DEFAULT_DIAGRAM_TYPE, diagramService } from '../services/diagramService';

const EMPTY_SVG_DATA_URL = '';
const ERROR_RENDER_DIAGRAM_FAILED = 'Failed to render diagram';

/**
 * Custom hook for rendering Structurizr diagrams with Kroki
 * @return Object with renderDiagram function and loading/error states
 */
export const useKrokiRenderer = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Render diagram using Kroki service via POST
   * @param dslContent - The Structurizr DSL content
   * @param diagramType - Type of diagram (default: 'structurizr')
   * @param viewKey - Optional view key for specific C4 view rendering
   * @return Promise<string> - Data URL for the SVG image
   */
  const renderDiagram = useCallback(
    async (
      dslContent: string,
      diagramType: string = DEFAULT_DIAGRAM_TYPE,
      viewKey?: string,
    ): Promise<string> => {
      if (!dslContent.trim()) {
        setError(null);
        return EMPTY_SVG_DATA_URL;
      }

      try {
        setIsLoading(true);
        setError(null);

        return await diagramService.renderDiagram(
          dslContent,
          diagramType,
          viewKey,
        );
      } catch (err) {
        const errorMessage = err instanceof Error ?
          err.message :
          ERROR_RENDER_DIAGRAM_FAILED;
        setError(errorMessage);
        return EMPTY_SVG_DATA_URL;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    renderDiagram,
    isLoading,
    error,
  };
};
